import { httpsCallable } from "firebase/functions";
import type { ProposalOutput } from "../types";
import { auth, firebaseConfig, functions } from "../firebase";
import { getFunctionErrorMessage } from "./functionErrors";

type CallableInvokerOptions = {
  fallbackMessage: string;
};

type HttpInvokerOptions = {
  method?: "POST" | "GET";
  requireAuth?: boolean;
  fallbackMessage: string;
};

type DevPlanGenerationResult = {
  jobId?: string;
  existing?: boolean;
  status?: string;
  reason?: string;
};

type CheckoutSessionResult = {
  url?: string;
};

type ConfirmCheckoutResult = {
  paid?: boolean;
  tier?: "high" | "mid" | "low";
  message?: string;
};

type MarketingAttributionPayload = {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  landingPath?: string;
  initialReferrer?: string;
  capturedAtMs?: number;
};

const DEFAULT_CALLABLE_FALLBACK = "Could not complete this action right now.";
const DEFAULT_HTTP_FALLBACK = "Could not reach the server right now.";

// Callable Cloud Functions must be invoked through httpsCallable.
async function callCallable<TInput, TOutput>(
  name: string,
  payload: TInput,
  options?: Partial<CallableInvokerOptions>
): Promise<TOutput> {
  const fallbackMessage = options?.fallbackMessage || DEFAULT_CALLABLE_FALLBACK;
  try {
    const callable = httpsCallable<TInput, TOutput>(functions, name);
    const result = await callable(payload);
    return result.data;
  } catch (error) {
    console.error(`[functions] callable ${name} failed`, error);
    const normalizedMessage = getFunctionErrorMessage(error, fallbackMessage);
    throw new Error(normalizedMessage);
  }
}

function functionUrl(name: string): string {
  const projectId = firebaseConfig.projectId;
  const isLocal = process.env.NODE_ENV === "development" || window.location.hostname === "localhost";
  if (isLocal) {
    return `http://localhost:5001/${projectId}/us-central1/${name}`;
  }
  return `https://us-central1-${projectId}.cloudfunctions.net/${name}`;
}

// genProposal remains HTTP on backend, but is wrapped here for consistent frontend usage.
async function callHttpFunction<TInput, TOutput>(
  name: string,
  payload: TInput,
  options?: Partial<HttpInvokerOptions>
): Promise<TOutput> {
  const method = options?.method || "POST";
  const requireAuth = options?.requireAuth ?? true;
  const fallbackMessage = options?.fallbackMessage || DEFAULT_HTTP_FALLBACK;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (requireAuth) {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("Please sign in to continue.");
    }
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(functionUrl(name), {
      method,
      headers,
      body: method === "GET" ? undefined : JSON.stringify(payload),
    });
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : {};
    if (!response.ok) {
      throw new Error(getFunctionErrorMessage(parsed, fallbackMessage));
    }
    return parsed as TOutput;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(getFunctionErrorMessage(error, fallbackMessage));
  }
}

export async function genProposalServer(proposalId: string, inputs: unknown): Promise<{ jobId: string }> {
  return callHttpFunction<{ proposalId: string; inputs: unknown }, { jobId: string }>(
    "genProposal",
    { proposalId, inputs },
    { fallbackMessage: "Could not start proposal generation right now." }
  );
}

export async function updateProposalSectionServer(
  proposalId: string,
  sectionKey: keyof ProposalOutput,
  value: unknown
): Promise<void> {
  await callCallable<{ proposalId: string; sectionKey: keyof ProposalOutput; value: unknown }, unknown>(
    "updateProposalSection",
    { proposalId, sectionKey, value },
    { fallbackMessage: "Could not save this proposal section right now." }
  );
}

export async function updateDevPlanFieldsServer(
  proposalId: string,
  patch: Record<string, unknown>
): Promise<void> {
  await callCallable<{ proposalId: string; patch: Record<string, unknown> }, unknown>(
    "updateDevPlanFields",
    { proposalId, patch },
    { fallbackMessage: "Could not save this build plan section right now." }
  );
}

export async function startDevPlanGenerationServer(
  proposalId: string,
  regenerate: boolean
): Promise<DevPlanGenerationResult> {
  return callCallable<{ proposalId: string; regenerate: boolean }, DevPlanGenerationResult>(
    "startDevPlanGeneration",
    { proposalId, regenerate },
    { fallbackMessage: "Could not start build plan generation right now." }
  );
}

export async function createCheckoutSessionServer(payload: {
  proposalId: string;
  tier: "high" | "mid" | "low";
  email: string;
  notes: string | null;
  summary: {
    mvpScope: string[];
    timelineWeeks: number;
    investmentRange: string;
  };
  attribution?: MarketingAttributionPayload | null;
}): Promise<CheckoutSessionResult> {
  return callCallable<typeof payload, CheckoutSessionResult>("createCheckoutSession", payload, {
    fallbackMessage: "Could not start payment right now.",
  });
}

export async function confirmCheckoutSessionServer(payload: {
  sessionId: string;
  proposalId: string;
}): Promise<ConfirmCheckoutResult> {
  return callCallable<typeof payload, ConfirmCheckoutResult>("confirmCheckoutSession", payload, {
    fallbackMessage: "Could not confirm payment right now.",
  });
}

export async function registerFreeDownloadServer(payload: {
  proposalId: string;
  email: string;
  authProvider: "email_password" | "google";
  summary: {
    mvpScope: string[];
    timelineWeeks: number;
    investmentRange: string;
  };
  attribution?: MarketingAttributionPayload | null;
}): Promise<void> {
  await callCallable<typeof payload, unknown>("registerFreeDownload", payload, {
    fallbackMessage: "Could not complete free download right now.",
  });
}

export async function generateFeatureDesignServer(payload: {
  proposalId: string;
  output: unknown;
}): Promise<unknown> {
  return callCallable<typeof payload, unknown>("callableGenerateFeatureDesign", payload, {
    fallbackMessage: "Could not regenerate feature design right now.",
  });
}

export async function generateUXScenarioServer(payload: {
  proposalId: string;
  feature: string;
}): Promise<unknown> {
  return callCallable<typeof payload, unknown>("callableGenerateUXScenario", payload, {
    fallbackMessage: "Could not regenerate UX scenarios right now.",
  });
}

export async function generateUIDesignServer(payload: {
  proposalId: string;
  feature: string;
  scenario: string;
}): Promise<unknown> {
  return callCallable<typeof payload, unknown>("callableGenerateUIDesign", payload, {
    fallbackMessage: "Could not regenerate UI design right now.",
  });
}

export async function generateDataModelServer(payload: {
  proposalId: string;
  feature: string;
  scenario: string;
  uiDesign: string;
}): Promise<unknown> {
  return callCallable<typeof payload, unknown>("callableGenerateDataModel", payload, {
    fallbackMessage: "Could not regenerate data model right now.",
  });
}

export async function generateUICodeServer(payload: {
  proposalId: string;
  feature: string;
  scenario: string;
  uiDesign: string;
  dataModel: string;
}): Promise<unknown> {
  return callCallable<typeof payload, unknown>("callableGenerateUICode", payload, {
    fallbackMessage: "Could not regenerate UI code right now.",
  });
}

export async function notifyButtonTapToSlackServer(payload: {
  userId: string;
  buttonLabel: string;
}): Promise<void> {
  await callCallable<typeof payload, unknown>("notifyButtonTapToSlack", payload, {
    fallbackMessage: "Could not send button tap notification right now.",
  });
}

export async function notifyWebAppErrorToSlackServer(payload: {
  errorMessage: string;
  pagePath: string;
  pageUrl: string;
  userAgent: string;
  userId: string;
}): Promise<void> {
  await callCallable<typeof payload, unknown>("notifyWebAppErrorToSlack", payload, {
    fallbackMessage: "Could not send error notification right now.",
  });
}
